import React, { memo, useEffect, useState, useRef } from 'react';
import Typography from '@mui/material/Typography';
import SettingsIcon from '@mui/icons-material/SettingsRounded';
import Content from 'studio/components/Content/Content';
import { trackEvent } from 'utils/eventTracking';
import { useMessage } from 'studio/components/Message';
import { useLoader } from 'components/loaders/FadeLoader';
import { useSystemZeroModels } from 'hooks/useSystemZeroModels';
import services from 'services';
import Select from 'studio/components/Select';
import PageTitle from 'studio/components/PageTitle';
import { formatNumber } from 'utils/numberFormat';
import { ThemeSwitcher } from 'components/settings/ThemeSwitcher';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from 'studio/components/Alert';
import { settingsSections } from 'studio/App/Admin/Admin';

import { useStyles } from './Settings.styles';

// the sample number to use for number format examples
const sampleNumber = 1234.56789;

const Settings = ({ initialSection, user }) => {
  // Add a ref for the Content component's scrollable element
  const contentRef = useRef(null);

  useEffect(() => {
    trackEvent('Admin Settings Opened');
  }, []);

  const { successMessage, errorMessage } = useMessage();
  const { withLoader } = useLoader();
  const { classes } = useStyles();

  // the settings
  const [settings, setSettings] = useState(null);
  // the supported number formats
  const [numberFormats, setNumberFormats] = useState(null);
  // Use shared hook for model data
  const { availableModels } = useSystemZeroModels();

  // the number format corresponding to the default number format from the settings
  const numberFormat = numberFormats?.find(
    numberFormat =>
      numberFormat.data.locale === settings?.data.defaultNumberFormat.data.locale &&
      numberFormat.data.pattern === settings?.data.defaultNumberFormat.data.pattern
  );

  // Scroll to the specified section when component mounts
  useEffect(() => {
    if (settings && numberFormats) {
      if (initialSection) {
        const element = document.getElementById(initialSection);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      } else {
        // No initial section means main settings page - scroll to top
        // Scroll the Content component's scrollable element instead of window
        if (contentRef.current) {
          contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }
  }, [initialSection, settings, numberFormats]);

  // loads/reloads the settings
  const reloadSettings = async () => {
    const response = await withLoader(services.getSettings());
    setSettings(response.data);
  };

  // loads/reloads the number formats
  const reloadNumberFormats = async () => {
    const response = await services.getNumberFormats();
    const numberFormats = response.data.data;
    setNumberFormats(numberFormats);
  };

  useEffect(() => {
    reloadSettings();
    reloadNumberFormats();
  }, []);

  // Handle LLM data access toggle change
  const handleLlmDataAccessToggle = async newValue => {
    try {
      // only update llm data access setting
      const updatedSettings = {
        isLlmDataAccessAllowed: newValue
      };

      const response = await withLoader(services.updateSettings(updatedSettings));
      if (response.success) {
        // we get back the most recent settings in the response, use that to update all the settings
        setSettings(response.data);
        successMessage(`LLM data access has been ${newValue ? 'enabled' : 'disabled'} for your organization`);
      } else {
        errorMessage(`Failed to update LLM data access setting.`);
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      errorMessage('Failed to update LLM data access setting.');
    }
  };

  // Handle model selection change
  const handleModelChange = async selectedModelId => {
    try {
      const updatedSettings = {
        // Use null for "Default" to reset to default, otherwise use the model ID
        systemZeroModelOverrideOpt: selectedModelId === 'Default' ? null : selectedModelId
      };

      const response = await withLoader(services.updateSettings(updatedSettings));
      if (response.success) {
        // Reload settings to get the updated state from server
        await reloadSettings();
        const selectedModel = availableModels?.find(model => model.id === selectedModelId);
        successMessage(`Veezoo Neo model has been updated to: ${selectedModel?.name || 'Default'}`);
      } else {
        errorMessage('Failed to update model setting.');
      }
    } catch (error) {
      console.error('Error updating model:', error);
      errorMessage('Failed to update model setting.');
    }
  };

  return (
    <Content loading={!settings || !numberFormats || !availableModels} data-test="settingsContent" ref={contentRef}>
      <PageTitle icon={SettingsIcon}>Settings</PageTitle>

      <div className={classes.section}>
        <Typography variant="h2" gutterBottom>
          Appearance
        </Typography>
        <Typography variant="body1" gutterBottom>
          Configure the appearance of Veezoo.
        </Typography>

        <div id={settingsSections.numberFormat} className={classes.setting}>
          <Typography variant="h3" gutterBottom>
            Number Format
          </Typography>
          <Typography variant="body1" gutterBottom>
            Set the default number format to use.
          </Typography>
          <div className={classes.input}>
            <Select
              labels={numberFormats?.map(numberFormat => formatNumber(sampleNumber, numberFormat))}
              // use the array index as the value but convert to a string s.t. it also works when selecting the first item (value 0)
              values={numberFormats?.map((numberFormat, index) => `${index}`)}
              value={'' + numberFormats?.indexOf(numberFormat)}
              onChange={async event => {
                const numberFormat = numberFormats[event.target.value];

                // only update selected number format
                const updatedSettings = {
                  defaultNumberFormat: {
                    locale: numberFormat.data.locale,
                    pattern: numberFormat.data.pattern
                  }
                };
                const response = await withLoader(services.updateSettings(updatedSettings));
                if (response.success) {
                  // we get back the most recent settings in the response, use that to update all the settings
                  setSettings(response.data);
                  successMessage(
                    `The default number format has been updated to: ${formatNumber(sampleNumber, numberFormat)}`
                  );
                } else {
                  errorMessage(`Failed to update number format`);
                }
              }}
            />
          </div>
          <Typography variant="body1" gutterBottom>
            Users can override the default by setting a preferred number format in their profile settings.
          </Typography>
        </div>

        <div id={settingsSections.changeTheme} className={classes.setting}>
          <Typography variant="h3" gutterBottom>
            Change Theme
          </Typography>
          <ThemeSwitcher />
        </div>
      </div>

      {user.isAdmin && (
        <div id={settingsSections.modelConfiguration} className={classes.section}>
          <Typography variant="h2" gutterBottom>
            Model Configuration
          </Typography>
          <Typography variant="body1" gutterBottom>
            Select the AI model to use for question answering in the Chat in Veezoo Neo mode.
          </Typography>
          <div className={classes.setting}>
            <div className={classes.input}>
              <Select
                label="Model"
                labels={availableModels?.map(model => model.name || model.id) || []}
                values={availableModels?.map(model => model.id) || []}
                itemStyles={
                  availableModels?.map(model =>
                    model.isDefault ? { color: 'text.secondary', fontStyle: 'italic' } : {}
                  ) || []
                }
                selectedStyle={
                  settings?.data?.systemZeroModelOverrideOpt === 'Default' ||
                  !settings?.data?.systemZeroModelOverrideOpt
                    ? { color: 'text.secondary', fontStyle: 'italic' }
                    : {}
                }
                value={settings?.data?.systemZeroModelOverrideOpt || 'Default'}
                onChange={async event => {
                  await handleModelChange(event.target.value);
                }}
                data-test="model-select"
              />
            </div>
          </div>
        </div>
      )}

      <div id={settingsSections.queryResultAccess} className={classes.section}>
        <Typography variant="h2" gutterBottom>
          LLM Query Result Access
        </Typography>
        <div className={classes.setting}>
          <Typography variant="body1" gutterBottom>
            Toggle whether third-party LLMs (e.g., Anthropic, Google, OpenAI) can access database query results from
            your connected databases.
          </Typography>

          <div className={classes.input}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings?.data?.isLlmDataAccessAllowed || false}
                  color="primary"
                  onChange={event => handleLlmDataAccessToggle(event.target.checked)}
                />
              }
              label={
                <Typography variant="body1">
                  {settings?.data?.isLlmDataAccessAllowed ? 'Enabled' : 'Disabled'}
                </Typography>
              }
              data-test="llm-data-access-switch"
            />
          </div>
          <Alert severity="info">
            <Typography variant="body1" gutterBottom>
              Enabling this option activates the <strong>possibility</strong> of third-party LLM query result access
              across your organization. To allow access in practice, you{' '}
              <strong>must also enable the corresponding option</strong> in Veezoo Studio for those Knowledge Graphs for
              which you want LLMs to access corresponding database query results.
            </Typography>

            <Typography variant="body1" gutterBottom>
              <strong>Why enable this?</strong> Allowing LLMs to access database query results can significantly improve
              the quality of answers by enabling capabilities like result summarization, better follow-up questions, and
              more natural explanations.
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>What if disabled?</strong> LLMs will be completely blocked from accessing database query results.
              They will still have access to schema-level information (i.e., table names and columns) and selected data
              values (e.g., individual product names) to be able to generate the right queries.
            </Typography>
          </Alert>
        </div>
      </div>
    </Content>
  );
};

export default memo(Settings);
